import React, { useState, useEffect } from 'react';
import { apiClient } from '@/api/client';

interface RevenueData {
  date: string;
  total: number;
}

interface PerformanceData {
  name: string;
  value: number;
  percentage?: number;
}

interface AnalyticsDashboardProps {
  branchId: string;
  isLoading?: boolean;
}

export function AnalyticsDashboard({ branchId, isLoading = false }: AnalyticsDashboardProps) {
  const [activeTab, setActiveTab] = useState<'revenue' | 'products' | 'staff' | 'inventory' | 'accounts'>('revenue');
  const [dateRange, setDateRange] = useState<{ start: string; end: string }>({
    start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0],
  });
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(isLoading);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    loadAnalytics();
  }, [activeTab, dateRange, branchId]);

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      setLoadError('');
      setData(null); // never render one tab's rows with another tab's fields
      let result = null;

      switch (activeTab) {
        case 'revenue':
          const revenueRes = await apiClient.getRevenueAnalytics(branchId, dateRange.start, dateRange.end, 'daily');
          result = revenueRes.data;
          break;
        case 'products':
          const productsRes = await apiClient.getProductPerformanceAnalytics(branchId, dateRange.start, dateRange.end, 15);
          result = productsRes.data;
          break;
        case 'staff':
          const staffRes = await apiClient.getStaffPerformanceAnalytics(branchId, dateRange.start, dateRange.end);
          result = staffRes.data;
          break;
        case 'inventory':
          const inventoryRes = await apiClient.getInventoryTurnover(branchId, 30);
          result = inventoryRes.data;
          break;
        case 'accounts':
          const accountsRes = await apiClient.getAccountAnalytics(branchId);
          result = accountsRes.data;
          break;
      }

      setData(result);
    } catch (error: any) {
      console.error('Failed to load analytics:', error);
      setData(null);
      setLoadError(error?.response?.data?.message || 'Could not load this report');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Date Range Selector */}
      <div className="bg-white rounded-lg shadow p-4 flex gap-4 items-end">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
          <input
            type="date"
            value={dateRange.start}
            onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
          <input
            type="date"
            value={dateRange.end}
            onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white rounded-lg shadow">
        <div className="flex border-b border-gray-200">
          <button
            onClick={() => setActiveTab('revenue')}
            className={`flex-1 py-4 text-center font-medium ${
              activeTab === 'revenue' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-600'
            }`}
          >
            💰 Revenue
          </button>
          <button
            onClick={() => setActiveTab('products')}
            className={`flex-1 py-4 text-center font-medium ${
              activeTab === 'products' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-600'
            }`}
          >
            📦 Products
          </button>
          <button
            onClick={() => setActiveTab('staff')}
            className={`flex-1 py-4 text-center font-medium ${
              activeTab === 'staff' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-600'
            }`}
          >
            👥 Staff
          </button>
          <button
            onClick={() => setActiveTab('inventory')}
            className={`flex-1 py-4 text-center font-medium ${
              activeTab === 'inventory' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-600'
            }`}
          >
            📊 Inventory
          </button>
          <button
            onClick={() => setActiveTab('accounts')}
            className={`flex-1 py-4 text-center font-medium ${
              activeTab === 'accounts' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-600'
            }`}
          >
            💳 Accounts
          </button>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {loading ? (
            <div className="text-center py-8 text-gray-500">Loading analytics...</div>
          ) : !data || Object.keys(data).length === 0 ? (
            <div className="text-center py-8 text-gray-500">No data available for selected period</div>
          ) : (
            <>
              {loadError && (
                <div className="bg-red-50 text-red-700 rounded-lg px-4 py-3 text-sm font-medium mb-4">
                  {loadError}
                </div>
              )}

              {!loadError && Array.isArray(data) && data.length === 0 && (
                <div className="py-10 text-center">
                  <p className="text-gray-500">Nothing recorded for this period.</p>
                  <p className="text-sm text-gray-400 mt-1">Try widening the date range.</p>
                </div>
              )}

              {activeTab === 'revenue' && (
                <div className="space-y-4">
                  <h3 className="font-bold text-gray-900">Revenue Over Time</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left p-2">Date</th>
                          <th className="text-right p-2">Total Revenue</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(Array.isArray(data) ? data : []).map((row: any, idx: number) => (
                          <tr key={idx} className="border-b hover:bg-gray-50">
                            <td className="p-2">{row.date}</td>
                            <td className="text-right p-2 font-semibold text-green-600">R{Number(row.total ?? 0).toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {activeTab === 'products' && (
                <div className="space-y-4">
                  <h3 className="font-bold text-gray-900">Top Selling Products</h3>
                  <div className="space-y-2">
                    {(Array.isArray(data) ? data : []).map((product: any, idx: number) => (
                      <div key={idx} className="border border-gray-200 rounded p-3">
                        <div className="flex justify-between items-center mb-2">
                          <span className="font-medium text-gray-900">{product.name ?? product.productName ?? '—'}</span>
                          <span className="text-sm text-gray-600">{product.unitsSold} units</span>
                        </div>
                        <div className="flex justify-between items-center text-sm">
                          <div className="flex-1 bg-gray-200 rounded-full h-2 mr-3">
                            <div
                              className="bg-blue-500 h-2 rounded-full"
                              style={{ width: `${((Number(product.totalRevenue ?? 0) / (Number(data?.[0]?.totalRevenue) || 1)) * 100)}%` }}
                            />
                          </div>
                          <span className="font-semibold text-green-600">R{Number(product.totalRevenue ?? 0).toFixed(2)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {activeTab === 'staff' && (
                <div className="space-y-4">
                  <h3 className="font-bold text-gray-900">Staff Performance</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b bg-gray-50">
                          <th className="text-left p-3 font-semibold">Staff</th>
                          <th className="text-center p-3 font-semibold">Sales Count</th>
                          <th className="text-right p-3 font-semibold">Total Revenue</th>
                          <th className="text-right p-3 font-semibold">Avg Sale Value</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(Array.isArray(data) ? data : []).map((staff: any, idx: number) => (
                          <tr key={idx} className="border-b hover:bg-gray-50">
                            <td className="p-3 font-medium">{staff.staffName}</td>
                            <td className="text-center p-3">{staff.salesCount}</td>
                            <td className="text-right p-3 font-semibold text-green-600">R{Number(staff.totalRevenue ?? 0).toFixed(2)}</td>
                            <td className="text-right p-3 text-gray-600">R{Number(staff.avgSaleValue ?? 0).toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {activeTab === 'inventory' && (
                <div className="space-y-4">
                  <h3 className="font-bold text-gray-900">Inventory Turnover</h3>
                  <div className="space-y-2">
                    {(Array.isArray(data) ? data : []).map((item: any, idx: number) => (
                      <div key={idx} className="border border-gray-200 rounded p-3">
                        <div className="flex justify-between items-center mb-2">
                          <span className="font-medium text-gray-900">{item.name ?? item.productName ?? '—'}</span>
                          <span className={`px-2 py-1 rounded text-xs font-bold ${item.turnover > 2 ? 'bg-green-100 text-green-800' : item.turnover > 1 ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'}`}>
                            {Number(item.turnover ?? 0).toFixed(1)}x
                          </span>
                        </div>
                        <div className="text-sm text-gray-600">
                          Sold: {item.unitsSold} units | Current Stock: {item.currentStock}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {activeTab === 'accounts' && (
                <div className="space-y-4">
                  <h3 className="font-bold text-gray-900">Customer Accounts</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b bg-gray-50">
                          <th className="text-left p-3 font-semibold">Account Name</th>
                          <th className="text-center p-3 font-semibold">Balance</th>
                          <th className="text-center p-3 font-semibold">Transactions</th>
                          <th className="text-left p-3 font-semibold">Last Transaction</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(Array.isArray(data) ? data : []).map((account: any, idx: number) => (
                          <tr key={idx} className="border-b hover:bg-gray-50">
                            <td className="p-3 font-medium">{account.accountName}</td>
                            <td className={`text-center p-3 font-semibold ${account.balance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              R{Number(account.balance ?? 0).toFixed(2)}
                            </td>
                            <td className="text-center p-3">{account.transactionCount}</td>
                            <td className="p-3 text-gray-600">
                              {account.lastTransaction ? new Date(account.lastTransaction).toLocaleDateString() : 'Never'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
